import React, { useEffect, useState } from "react";
import { Building2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useBusiness } from "@/context/BusinessContext";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "due_soon", label: "Due Soon" },
  { key: "waiting_approval", label: "Needs you" },
  { key: "expired", label: "Expired" },
  { key: "submitted", label: "Submitted" },
  { key: "completed", label: "Completed" },
];

export default function Portfolio() {
  const [rows, setRows] = useState([]);
  const [filter, setFilter] = useState("all");
  const { switchBusiness } = useBusiness();

  useEffect(() => { api.get("/portfolio").then(({ data }) => setRows(data)).catch(() => {}); }, []);

  const shown = filter === "all" ? rows : rows.filter((r) => (r[filter] || 0) > 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="font-display font-black text-2xl mb-1 flex items-center gap-2"><Building2 size={22} className="text-dox-orange" /> All Businesses</h2>
        <p className="text-white/55 text-sm">Accountant Mode — manage every business you have access to from one place.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.key} data-testid={`portfolio-filter-${f.key}`} onClick={() => setFilter(f.key)}
            className={`rounded-full px-4 py-2 text-xs font-bold transition-colors ${filter === f.key ? "bg-dox-orange text-white" : "glass text-white/70 hover:bg-white/10"}`}>
            {f.label}
          </button>
        ))}
      </div>

      {shown.length === 0 ? (
        <div className="text-center py-12 text-white/40 text-sm">No businesses match this filter.</div>
      ) : (
        <div className="grid sm:grid-cols-2 gap-4">
          {shown.map((r) => (
            <button key={r.id} data-testid={`portfolio-biz-${r.id}`} onClick={() => switchBusiness(r.id)}
              className="text-left glass-card p-5 hover:border-dox-orange/40 transition-colors group">
              <div className="flex items-start justify-between">
                <div className="min-w-0">
                  <div className="font-display font-bold text-white truncate">{r.name}</div>
                  <div className="text-xs text-white/40">{r.industry}{r.states?.length ? ` · ${r.states.join(", ")}` : ""} · {r.role}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-black text-2xl text-gradient-brand">{r.compliance_score}%</div>
                  <div className="text-[10px] text-white/40 uppercase">Compliance</div>
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 mt-4 text-center">
                <Mini label="Docs" value={r.documents} />
                <Mini label="Due" value={r.due_soon} tone="text-dox-magenta" />
                <Mini label="Needs you" value={r.waiting_approval} tone="text-amber-400" />
                <Mini label="Attention" value={r.needs_attention} tone="text-red-400" />
              </div>
              <div className="mt-4 flex items-center gap-1 text-xs text-dox-orange opacity-0 group-hover:opacity-100 transition-opacity">
                Open dashboard <ArrowRight size={12} />
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function Mini({ label, value, tone = "text-white" }) {
  return (
    <div className="rounded-lg bg-white/[0.03] border border-white/5 py-2">
      <div className={`font-display font-black text-lg ${tone}`}>{value}</div>
      <div className="text-[10px] text-white/40 uppercase tracking-wider">{label}</div>
    </div>
  );
}
